/**
 * labor-core パッケージ一覧の query 窓口。
 * OWNER_PRODUCT（'HRF_LABOR_CORE'）を固定した薄いラッパーとして
 * `@/features/operation-packages` の実 API・実 Query 層を公開する。
 */
import { useQuery } from '@tanstack/react-query';
import type { GovernmentApplicationAggregateTableRowData } from '@/features/labor-core/-components/government-application-aggregate/types';
import { getGovernmentApplicationPackageInstances, getOperationPackageCountInProgress as getOperationPackageCountInProgressApi, getOperationPackagePatterns as getOperationPackagePatternsApi, getOperationPackages as getOperationPackagesApi, startPattern as assignTaskToPersonApi, type OperationPackagesResponse, type StartPatternResponse } from '@/features/operation-packages/api';
import { operationPackageQueryKeys, useGetOperationPackageInstances as useGetOperationPackageInstancesCommon, useGetOperationPackagePatterns as useGetOperationPackagePatternsCommon, useGetOperationPackages as useGetOperationPackagesCommon, useGetOperationTaskCountInProgress as useGetOperationTaskCountInProgressCommon, usePostOperationPackagePatterns as usePostOperationPackagePatternsCommon } from '@/features/operation-packages/hooks';
import { mapGovernmentApplicationInstanceToTableRowData } from '@/features/operation-packages/mapGovernmentApplicationInstanceToTableRowData';
import { QUERY_PRESETS } from '@/lib/query';
const OWNER_PRODUCT = 'HRF_LABOR_CORE';

// --- API Wrappers (for usage outside hooks if needed) ---

export type GetOperationPackagesRes = OperationPackagesResponse;
export const getOperationPackages = (): Promise<OperationPackagesResponse> => getOperationPackagesApi(OWNER_PRODUCT);
export const getOperationPackageCountInProgress = (id: string) => getOperationPackageCountInProgressApi(OWNER_PRODUCT, id);
export const getOperationPackagePatterns = (id: string) => getOperationPackagePatternsApi(OWNER_PRODUCT, id);
export const assignTaskToPerson = (id: string, patternId: string, body: {
  targetPersonIds: string[];
  anchorDate: string;
  tagName?: string;
}): Promise<StartPatternResponse> => assignTaskToPersonApi(OWNER_PRODUCT, id, patternId, body);

// --- Hooks Wrappers ---

export const useGetOperationPackages = () => useGetOperationPackagesCommon(OWNER_PRODUCT);
export const useGetOperationPackagePatterns = (id: string, options?: {
  enabled?: boolean;
}) => useGetOperationPackagePatternsCommon(OWNER_PRODUCT, id, options);
export const usePostOperationPackagePatterns = (id: string, options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) => usePostOperationPackagePatternsCommon(OWNER_PRODUCT, id, options);
export const useGetOperationTaskCountInProgress = (params: {
  operationPackageId: string;
  patternId: string;
}) => useGetOperationTaskCountInProgressCommon({
  ownerProduct: OWNER_PRODUCT,
  ...params
});
export const useGetOperationPackageInstances = (patternId: string, options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  targetId?: string;
}) => useGetOperationPackageInstancesCommon(OWNER_PRODUCT, patternId, options);
type GovernmentApplicationInstancesData = {
  items: GovernmentApplicationAggregateTableRowData[];
  count: number;
  maxPage: number;
  length: number;
  page: number;
};

/**
 * 行政申請系パッケージ（GovernmentApplication / YearEndAdjustment 等）専用の Step 列付き
 * インスタンス一覧。実 API レスポンスを集計テーブル行データへ変換する。
 */
export const useGetGovernmentApplicationPackageInstances = (patternId: string, options?: {
  enabled?: boolean;
  page?: number;
  pageSize?: number;
  targetId?: string;
}) => {
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 50;
  const targetId = options?.targetId;
  const {
    data,
    isPending,
    error
  } = useQuery({
    ...QUERY_PRESETS.list(),
    queryKey: operationPackageQueryKeys.governmentApplicationInstances(OWNER_PRODUCT, patternId, page, pageSize, targetId),
    queryFn: async (): Promise<GovernmentApplicationInstancesData> => {
      const response = await getGovernmentApplicationPackageInstances(OWNER_PRODUCT, patternId, {
        page,
        pageSize,
        targetId
      });
      return {
        ...response,
        items: response.items.map(row => mapGovernmentApplicationInstanceToTableRowData(row))
      };
    },
    enabled: options?.enabled ?? true
  });
  return {
    data,
    isPending,
    error
  };
};